import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
function useCountUp(end: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasStarted) {
        setHasStarted(true);
      }
    }, {
      threshold: 0.5
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted, startOnView]);
  useEffect(() => {
    if (!hasStarted) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [hasStarted, end, duration]);
  return {
    count,
    ref
  };
}
function CountStat({
  value,
  prefix = '',
  suffix = '',
  label
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
}) {
  const {
    count,
    ref
  } = useCountUp(value, 2000);
  return <div ref={ref} data-id="element-135">
      <div className="text-2xl font-bold text-white mb-1" data-id="element-136">
        {prefix}
        {count.toLocaleString()}
        {suffix}
      </div>
      <div className="text-sm text-gray-400 font-medium" data-id="element-137">{label}</div>
    </div>;
}
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-brand-dark" data-id="element-138">
      {/* Background Image Overlay */}
      <div className="absolute inset-0 z-0 opacity-30 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/kETzi4UEwLeZ97nPiSA7JV/hurricane-season-mid-year-outlook-stage.webp")'
    }} data-id="element-139"></div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-transparent" data-id="element-140"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-141">
        <div className="max-w-3xl" data-id="element-142">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-143">
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-2" data-id="element-144">
              {t.hero.title}
            </h1>
            <h2 className="text-xl md:text-3xl font-bold text-brand-gold uppercase tracking-wide mb-4" data-id="element-145">
              {t.hero.subtitle}
            </h2>

            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-sm bg-brand-red/20 border border-brand-gold/30 mb-8 backdrop-blur-sm" data-id="element-146">
              <ShieldCheck className="text-brand-gold" size={24} data-id="element-147" />
              <span className="text-white font-black text-base md:text-lg uppercase tracking-[0.2em]" data-id="element-148">
                {t.hero.badge}
              </span>
            </div>

            <p className="text-xl text-gray-300 mb-10 max-w-2xl leading-relaxed" data-id="element-149">
              {t.hero.desc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-150">
              <Link to="/consultation" className="inline-flex justify-center items-center gap-2 bg-brand-red hover:bg-brand-darkred text-white px-8 py-4 rounded-sm font-bold text-lg transition-all transform hover:-translate-y-1 shadow-lg shadow-brand-red/30" data-id="element-151">
                {t.hero.cta1}
                <ArrowRight size={20} data-id="element-152" />
              </Link>
              <a href="#services" className="inline-flex justify-center items-center gap-2 bg-transparent border-2 border-white hover:border-brand-gold hover:text-brand-gold text-white px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-153">
                {t.hero.cta2}
              </a>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.8,
          duration: 0.8
        }} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8" data-id="element-154">
            <CountStat value={5} suffix="+" label={t.hero.stats.s1} data-id="element-155" />
            <CountStat value={24} suffix="/7" label={t.hero.stats.s2} data-id="element-156" />
            <CountStat value={500} suffix="+" label={t.hero.stats.s3} data-id="element-157" />
            <CountStat value={15} prefix="$" suffix="M+" label={t.hero.stats.s4} data-id="element-158" />
          </motion.div>
        </div>
      </div>
    </section>;
}